import { AnimatePresence, motion } from "motion/react";
import { bool, number, oneOfType, string } from "prop-types";
import { useRef, useState } from "react";

import styled from "styled-components";

import { useGetNetworkMetadata } from "Flocknote/API/queries";

import { menuTypographyProps } from "NavBar/components/NavLink";

import FnrTypography from "UI/FnrTypography";
import { colorsByFunction, navAccentColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import { formatCurrencyWithCommas } from "Utilities/Number";
import { accentShadow } from "Utilities/interpolations/shadow";

const Amount = ({ amount, showPeriodLabel = true }) => {
  const wrapperRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const currencyAmount = `$${formatCurrencyWithCommas(amount, false)}`;
  const { data: networkMetadata } = useGetNetworkMetadata();
  // When the network runs on a custom fiscal year, the nav totals are
  // fiscal-year-to-date rather than calendar year-to-date.
  const periodLabel = networkMetadata?.fiscalYearStart ? "FYTD" : "YTD";

  return (
    <AmountWrapper
      ref={wrapperRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FnrTypography
        {...menuTypographyProps}
        shadow={false}
        fontSize="xs"
        bold
        color={navAccentColors.giving[1]}
      >
        {currencyAmount}
      </FnrTypography>

      {showPeriodLabel && (
        <ExpandingAmount
          show={isHovered}
          currencyAmount={currencyAmount}
          periodLabel={periodLabel}
        />
      )}
    </AmountWrapper>
  );
};

Amount.propTypes = {
  amount: oneOfType([number, string]),
  showPeriodLabel: bool,
};

const AmountWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 24px;
  padding: 0px 8px;
  background-color: ${addOpacity(colorsByFunction.textLight, 0.95)};
  height: 22px;
  ${accentShadow};
  pointer-events: auto;
  position: relative;
  z-index: 1;
`;

const HoverAmountWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  border-radius: 24px;
  padding: 0px 8px;
  background-color: ${addOpacity(colorsByFunction.textLight, 0.95)};
  height: 22px;
  ${accentShadow};
  pointer-events: none;
  z-index: 2;
`;

const YTDContainer = styled(motion.div)`
  display: flex;
  align-items: center;
`;

export default Amount;

function ExpandingAmount({ show, currencyAmount, periodLabel }) {
  return (
    <AnimatePresence mode="wait">
      {show && (
        <HoverAmountWrapper
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0 }}
        >
          <FnrTypography
            {...menuTypographyProps}
            shadow={false}
            fontSize="xs"
            bold
            color={navAccentColors.giving[1]}
          >
            {currencyAmount}
          </FnrTypography>
          <YTDContainer
            initial={{ width: 0, opacity: 0, marginLeft: 0 }}
            animate={{ width: "auto", opacity: 1, marginLeft: 4 }}
            exit={{ width: 0, opacity: 0, marginLeft: 0 }}
            transition={{
              duration: 0.3,
              delay: show ? 0.05 : 0,
              ease: "easeInOut",
            }}
            style={{ overflow: "hidden" }}
          >
            <FnrTypography
              {...menuTypographyProps}
              shadow={false}
              fontSize="xs"
              bold
              color={navAccentColors.giving[1]}
            >
              {periodLabel}
            </FnrTypography>
          </YTDContainer>
        </HoverAmountWrapper>
      )}
    </AnimatePresence>
  );
}

ExpandingAmount.propTypes = {
  show: bool,
  currencyAmount: string,
  periodLabel: string,
};
